import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Divider,
  Grid,
  withStyles,
  WithStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import classnames from 'classnames';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { RouteLink } from '../../components/Shared/RouteLink';
import React, { useState, Fragment } from 'react';
import { compose } from 'recompose';
import { withLocale } from '../../hocs/withLocale';
import routes from '../../library/Routes';
import moment from 'moment';

const styles = (theme: Theme) =>
  createStyles({
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
  });

interface Teacher {
  name: string;
  avatar?: string;
  [key: string]: any;
}

interface Course {
  _id: string;
  title: string;
  avatar?: string;
  description?: string;
  color: string;
  teacherId: string[];
  start?: string;
  end?: string;
  allowBookEmptySlots?: boolean;
  allowBookCourse?: boolean;
  [key: string]: any;
}

interface Studio {
  domainName?: string;
  [key: string]: any;
}

interface SingleCourseViewProps extends WithStyles<typeof styles> {
  course: Course;
  teachersMap: Record<string, Teacher>;
  studio: Studio;
  translate: (key: string, data?: any) => string;
  className?: string;
  fixed?: boolean;
}

export const SingleCourseView = compose<React.ComponentType<SingleCourseViewProps>>(
  withStyles(styles),
  withLocale
)(({ course, teachersMap, studio, classes, translate, className, fixed = false }: SingleCourseViewProps) => {
  const [expand, setExpand] = useState<boolean>(fixed);

  const renderCourseDescription = () => {
    if (course.description) {
      return (
        <Typography component="div">
          <div dangerouslySetInnerHTML={{ __html: course.description }} />
        </Typography>
      );
    } else {
      return <Typography component="div">{translate('studio.noCourseDescription')}</Typography>;
    }
  };

  const renderCourseDates = () => {
    if (course.start && course.end) {
      let start = moment.utc(course.start);
      let end = moment.utc(course.end);
      return (
        <CardHeader
          subheader={
            <Fragment>
              {start.format('DD MMM YYYY')} - {end.format('DD MMM YYYY')}
            </Fragment>
          }
        />
      );
    }
    return null;
  };

  return (
    <Card style={{ border: `solid 1px ${course.color}` }} className={className}>
      <CardHeader
        onClick={() => {
          !fixed && setExpand(!expand);
        }}
        avatar={
          course.avatar ? (
            <Avatar src={course.avatar} />
          ) : (
            course.teacherId.map((item, index) => {
              let teacher = teachersMap[item];
              if (teacher && teacher.avatar) {
                return <Avatar key={index} src={teacher.avatar} />;
              }
              return null;
            })
          )
        }
        title={course.title}
        subheader={
          <Grid container spacing={1}>
            {course.teacherId.map((item, index) => {
              let teacher = teachersMap[item];
              if (teacher) {
                return (
                  <Grid item xs={12} key={index}>
                    {teacher.name}
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        }
        action={
          !fixed && (
            <IconButton
              className={classnames(classes.expand, {
                [classes.expandOpen]: expand,
              })}
              aria-expanded={expand}
            >
              <ExpandMoreIcon />
            </IconButton>
          )
        }
      />
      {renderCourseDates()}
      <CardContent>
        {course.allowBookEmptySlots && (
          <Button
            color="primary"
            component={RouteLink}
            to={routes.booking({
              domainName: studio.domainName,
              courseId: course._id,
            })}
            target="_blank"
          >
            {translate('studio.class.signUp')}
          </Button>
        )}
        {course.allowBookCourse && (
          <Button
            color="primary"
            component={RouteLink}
            to={routes.courseBooking({
              domainName: studio.domainName,
              courseId: course._id,
            })}
            target="_blank"
          >
            {translate('studio.course.book')}
          </Button>
        )}
      </CardContent>
      <Divider />
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <CardContent>{renderCourseDescription()}</CardContent>
      </Collapse>
    </Card>
  );
});
